import React, { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Body } from '@/components/atoms/typography'
import { Category } from '@/network/graphql.g'
import { validFor } from '@/theme/validFor'
import { NextRouter } from 'next/router'

type CategoryMenuCampaignRedesignProps = {
  categories: Category[]
  level?: number
  router: NextRouter
  campaignId: string
  setCategoryFilter: (categoryId: string) => void
}

const CategoryMenuCampaignRedesign: FC<CategoryMenuCampaignRedesignProps> = ({
  categories,
  level = 0,
  router,
  campaignId,
  setCategoryFilter
}) => {
  const [openIds, setOpenIds] = useState<string[]>([])

  const toggleCategory = (id: string) => {
    setOpenIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const currentCategoryId = useMemo(() => {
    const qs = router.asPath.split('?')[1] ?? ''
    const sp = new URLSearchParams(qs)
    const fromQs =
      sp.get('category[]') || sp.get('category') || sp.get('categoryId')
    if (fromQs) {
      return fromQs
    }

    const q = router.query as Record<string, string | string[] | undefined>
    const raw = q['category[]'] ?? q.category ?? q.categoryId
    return Array.isArray(raw) ? raw[0] : raw
  }, [router.asPath, router.query])

  const isCategoryInPath = (category: Category, currentId: string): boolean => {
    if (category.id === currentId) {
      return true
    }
    if (!category.children) {
      return false
    }
    return category.children.some((child) => isCategoryInPath(child, currentId))
  }

  return (
    <Wrapper>
      <StyledUl level={level}>
        {categories.map((cat) => {
          const hasChildren = cat.children && cat.children.length > 0
          const isOpened = openIds.includes(cat.id)
          const isLevel0 = level === 0
          const isActive = isCategoryInPath(cat, currentCategoryId ?? '')
          return (
            <li key={cat.id}>
              <Row
                isLevel0={isLevel0}
                onClick={
                  isLevel0 && hasChildren
                    ? () => toggleCategory(cat.id)
                    : undefined
                }
                style={{ userSelect: 'none' }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center' }}
                  onClick={() => {
                    setCategoryFilter(cat.id)
                  }}
                >
                  <StyledBody2
                    isLevel0={isLevel0}
                    highlight={cat.isHighlight}
                    isActive={!!isActive}
                  >
                    {cat.title}
                  </StyledBody2>
                </div>
              </Row>

              {hasChildren && ((isLevel0 && isOpened) || !isLevel0) && (
                <CategoryMenuCampaignRedesign
                  categories={cat.children}
                  level={level + 1}
                  router={router}
                  campaignId={campaignId}
                  setCategoryFilter={setCategoryFilter}
                />
              )}
            </li>
          )
        })}
      </StyledUl>
    </Wrapper>
  )
}

export default CategoryMenuCampaignRedesign

const Wrapper = styled.div`
  width: 180px;
  margin-right: 50px;
`

const StyledUl = styled.ul`
  list-style: none;
  padding-left: ${(props) => (props.level > 0 ? '18px' : '0')};
  margin-top: 0;
`
const StyledBody2 = styled(Body)`
  margin: 0;
  font-weight: ${(props) => (props.isActive ? '900' : '400')};
  font-size: ${(props) => (props.isLevel0 ? '16px' : '14px')};
  line-height: ${(props) => !props.isLevel0 && 1.72};
  ${validFor.mobile`
    font-size: 14px;
  `}
`
const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0;
  padding: 0;
  cursor: pointer;
`
